import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdAdd, MdRefresh, MdSearch, MdMenuBook, MdEdit, MdClose } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import Card from "components/card";
import { Skeleton } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import Select from "components/ui/Select";
import InlineRename from "components/ui/InlineRename";
import { UI_INPUT, UI_PRIMARY_BTN, UI_SECONDARY_BTN } from "lib/uiClasses";
import { instructorCoursesService, type InstructorCourse } from "services/instructorCoursesService";
import CoursePricingBadge from "../components/CoursePricingBadge";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";

function useDebounced<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function CourseCard({
  course,
  isArabic,
  onRename,
}: {
  course: InstructorCourse;
  isArabic: boolean;
  onRename: (course: InstructorCourse, next: string) => Promise<void>;
}) {
  const subject = (course.subject as any)?.name || (course.subject as any)?.title;
  const imageSrc = resolveStoredUploadPreview(course.image, course.image_url);
  return (
    <Link
      to={`/instructor/courses/${course.id}/overview`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md dark:border-white/10 dark:bg-navy-800"
    >
      <div className="relative h-32 w-full overflow-hidden bg-gradient-to-br from-brand-400 to-brand-600">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={course.title}
            className="h-full w-full object-cover"
            onError={(e) => ((e.currentTarget.style.display = "none"))}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-3xl font-bold uppercase text-white">
            {String(course.title || "?").slice(0, 2)}
          </div>
        )}
        {subject && (
          <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-bold text-brand-700 shadow">
            {subject}
          </span>
        )}
        <CoursePricingBadge course={course} isArabic={isArabic} className="absolute right-3 top-3 shadow" />
      </div>
      <div className="flex min-h-[120px] flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <InlineRename
            value={course.title}
            onSubmit={async (next) => {
              await onRename(course, next);
            }}
          >
            {({ edit, label }) => (
              <h3
                className="line-clamp-2 flex-1 text-base font-bold text-gray-800 dark:text-white"
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  edit();
                }}
                title={label}
              >
                {label}
              </h3>
            )}
          </InlineRename>
          <span
            className="shrink-0 rounded-md p-1 text-gray-400 opacity-0 hover:bg-gray-100 group-hover:opacity-100 dark:hover:bg-white/10"
            title={isArabic ? "اضغط مرتين للتعديل" : "Double click to rename"}
          >
            <MdEdit className="h-4 w-4" />
          </span>
        </div>
        {course.description ? (
          <p className="line-clamp-2 text-xs text-gray-500 dark:text-gray-300">{course.description}</p>
        ) : null}
        <div className="mt-auto flex items-center justify-between pt-2 text-[11px] text-gray-400">
          <span>#{course.id}</span>
          <span className="font-semibold text-brand-500 group-hover:underline">
            {isArabic ? "فتح →" : "Open →"}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CoursesListPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();

  const [items, setItems] = React.useState<InstructorCourse[]>([]);
  const [meta, setMeta] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(24);
  const [term, setTerm] = React.useState("");
  const debouncedTerm = useDebounced(term, 300);

  const fetchList = React.useCallback(
    async (targetPage = 1) => {
      setLoading(true);
      try {
        const res = await instructorCoursesService.list({
          limit,
          term: debouncedTerm.trim() || undefined,
          page: targetPage,
        } as any);
        setItems(Array.isArray(res.data) ? res.data : []);
        setMeta(res.meta || res);
        setPage(targetPage);
      } finally {
        setLoading(false);
      }
    },
    [limit, debouncedTerm]
  );

  React.useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchList(page);
    } finally {
      setRefreshing(false);
    }
  }, [fetchList, page]);

  const handleRename = async (course: InstructorCourse, nextTitle: string) => {
    const prev = items;
    setItems((p) => p.map((c) => (c.id === course.id ? { ...c, title: nextTitle } : c)));
    try {
      await instructorCoursesService.update(course.id, {
        title: nextTitle,
        description: course.description ?? null,
        image: course.image ?? null,
        subject_id: Number(course.subject_id ?? 0),
        grade_id: course.grade_id ?? course.subject?.grade_id ?? null,
      });
    } catch {
      setItems(prev);
      throw new Error("rename failed");
    }
  };

  const totalItems = Number(meta?.total || meta?.meta?.total || items.length || 0);
  const totalPages = Math.max(
    1,
    Number(meta?.last_page || meta?.meta?.last_page || Math.ceil(totalItems / Math.max(1, limit)))
  );

  return (
    <div className="w-full space-y-5 p-4 md:p-6">
      <header>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          {isArabic ? "الكورسات" : "Courses"}
        </h1>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-slate-400">
          {isArabic ? "كل كورساتك في مكان واحد." : "All your courses in one place."}
        </p>
      </header>

      <Card extra="w-full p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md lg:flex-1">
            <MdSearch
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={isArabic ? "ابحث في الكورسات..." : "Search courses..."}
              aria-label={isArabic ? "ابحث في الكورسات" : "Search courses"}
              className={`${UI_INPUT} h-11 ps-9 pe-9`}
            />
            {term ? (
              <button
                type="button"
                onClick={() => setTerm("")}
                aria-label={isArabic ? "مسح البحث" : "Clear search"}
                className="absolute end-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-slate-200"
              >
                <MdClose className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="courses-per-page">
                {isArabic ? "عدد العناصر" : "Items per page"}
              </label>
              <span className="hidden whitespace-nowrap text-xs font-medium text-gray-500 dark:text-slate-400 sm:inline">
                {isArabic ? "العدد" : "Show"}
              </span>
              <div className="w-full sm:w-24">
                <Select
                  id="courses-per-page"
                  value={limit}
                  onChange={(next) => setLimit(Number(next) || 24)}
                  options={[12, 24, 48, 96].map((n) => ({ value: n, label: String(n) }))}
                  isArabic={isArabic}
                  numeric
                  allowEmpty={false}
                  clearable={false}
                  className="h-11"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className={`${UI_SECONDARY_BTN} inline-flex h-11 w-full items-center justify-center gap-1.5 sm:w-auto`}
            >
              <MdRefresh className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              {isArabic ? "تحديث" : "Refresh"}
            </button>
            <button
              type="button"
              onClick={() => navigate("new")}
              className={`${UI_PRIMARY_BTN} inline-flex h-11 w-full items-center justify-center gap-1.5 sm:w-auto`}
            >
              <MdAdd className="h-4 w-4" />
              {isArabic ? "كورس جديد" : "New course"}
            </button>
          </div>
        </div>
      </Card>

      <Card extra="w-full p-4 md:p-6">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MdMenuBook className="h-6 w-6" />}
            title={isArabic ? "لا توجد كورسات" : "No courses yet"}
            description={
              isArabic
                ? "ابدأ بإنشاء كورسك الأول لإضافة المحتوى والاختبارات."
                : "Create your first course to add content and quizzes."
            }
            action={
              <button
                type="button"
                onClick={() => navigate("new")}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                <MdAdd className="h-4 w-4" />
                {isArabic ? "إنشاء كورس" : "Create course"}
              </button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((c) => (
              <CourseCard key={c.id} course={c} isArabic={isArabic} onRename={handleRename} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {isArabic ? "الإجمالي" : "Total"}: {totalItems}
            </span>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => fetchList(p)}
                  className={`h-8 w-8 rounded-lg text-sm font-semibold ${
                    p === page
                      ? "bg-brand-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
